// Builds and downloads the DOK Skills Checklist as a Word (.docx)
// document — a blank, printable worksheet a teacher can hand-mark
// while observing a student. Uses the same `docx` library + download
// technique (Packer.toBlob() + a temporary <a download> anchor click)
// as studentReportPdf.js, for the same CSP reason documented there:
// a blob: download isn't subject to the frame-src directive that
// blocks the old iframe/html2pdf approach.
//
// This mirrors the layout of the source paper form: title + Subject ·
// Unit header, blank Student name / Class / Date fields, one section
// per DOK level with an unfilled Em/Dv/Pr/Ex proficiency selector and
// a checkbox per skill, an Overall Proficiency row, and a blank
// Teacher's note area. Nothing here reads or writes saved assessment
// data — it only reads curriculum (Subject/Unit/Skill) data.
//
// Every size/color below comes from the THEME object — a single
// design-token source so the document can't drift into the kind of
// inconsistent, ad hoc styling (mismatched fonts, a different accent
// per section) an earlier version had.

import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    AlignmentType,
    BorderStyle,
    WidthType,
    ShadingType,
    TabStopType,
    LeaderType,
    convertInchesToTwip,
} from "docx";

// `size` is in half-points (docx convention) — 28 = 14pt, 22 = 11pt.
const THEME = {
    font: "Arial",

    size: {
        title: 28, // 14pt — document title
        sectionTitle: 24, // 12pt — "Recall & Reproduction" etc.
        body: 22, // 11pt — skill text, field labels, everything else readable
        meta: 18, // 9pt — counters, footer, legend labels
        badge: 16, // 8pt — DOK N / Em·Dv·Pr·Ex pill text
    },

    color: {
        // The one accent green used everywhere a brand color appears:
        // title, every DOK badge, every legend pill, the section accent
        // bar. One token, one color, used consistently — no per-section
        // hue variation.
        brand: "4A6741",
        brandBg: "EDF1E6", // light wash behind each DOK section header

        text: "262626",
        muted: "6B6F5E",
        border: "DCE0D2",
        stripe: "F7F8F3",
        white: "FFFFFF",
    },
};

const PROFICIENCY_LEGEND = [
    { abbr: "Em", label: "Emerging" },
    { abbr: "Dv", label: "Developing" },
    { abbr: "Pr", label: "Proficient" },
    { abbr: "Ex", label: "Extending" },
];

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: THEME.color.white };

const NO_CELL_BORDERS = {
    top: NO_BORDER,
    bottom: NO_BORDER,
    left: NO_BORDER,
    right: NO_BORDER,
};

const thinBorder = (color = THEME.color.border) => ({
    style: BorderStyle.SINGLE,
    size: 4,
    color,
});

const fullWidth = { size: 100, type: WidthType.PERCENTAGE };

const spacerParagraph = (after = 200) =>
    new Paragraph({ text: "", spacing: { after } });

const formatDate = (isoDate) =>
    new Date(isoDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });

// A single Em/Dv/Pr/Ex or "DOK N" pill — shaded-run background, no
// borders. Shared by the top legend and every section header so the
// badge treatment never diverges across the document. (An earlier
// version built these as individually-bordered table cells; Word
// collapses/merges adjoining cell borders unpredictably, which
// rendered as broken underlines instead of clean pills — shading has
// no such border-reconciliation problem.)
const buildPillRun = (text, { size = THEME.size.badge } = {}) =>
    new TextRun({
        text: `  ${text}  `,
        bold: true,
        size,
        color: THEME.color.white,
        shading: {
            type: ShadingType.CLEAR,
            fill: THEME.color.brand,
        },
    });

// An empty checkbox glyph. This MUST carry an explicit font: the
// document's default font is Arial, which has no glyph for the
// Unicode "ballot box" character — leaving the font unset forces
// Word to silently substitute some other installed font for just
// this character, and that substitution is resolved fresh (and can
// come out differently) every time the file is opened, which is why
// the box looked fine in some rows and broken/inconsistent in
// others, yet always looked fine once exported to PDF (PDF export
// freezes whatever got resolved at that moment). Wingdings char
// 0xF0A8 is the standard, guaranteed-present checkbox glyph Word
// itself inserts via Insert ▸ Symbol ▸ Wingdings — pinning both the
// character and the font makes the rendering deterministic.
const buildCheckboxRun = ({ size = THEME.size.body } = {}) =>
    new TextRun({
        text: "",
        font: "Wingdings",
        bold: true,
        size,
        color: THEME.color.muted,
    });

// ---------------------------------------------------------------
// Title + Student/Class/Date + instructions
// ---------------------------------------------------------------

const buildTitleParagraph = (subjectName, unitName) =>
    new Paragraph({
        tabStops: [
            { type: TabStopType.RIGHT, position: convertInchesToTwip(7) },
        ],
        spacing: { after: 160 },
        children: [
            new TextRun({
                text: "DOK Skills Checklist",
                bold: true,
                size: THEME.size.title,
                color: THEME.color.brand,
            }),
            new TextRun({ text: "\t" }),
            new TextRun({
                text: [subjectName, unitName].filter(Boolean).join(" · "),
                bold: true,
                size: THEME.size.body,
                color: THEME.color.brand,
            }),
        ],
    });

// Student name / Class / Date on a single line, each label followed
// by a blank field on the SAME line — a right-flush tab stop per
// field with an underscore leader draws the fill-in line, the same
// way a real paper form does, rather than a label on one line and a
// blank on the next.
const buildIdentityParagraph = () =>
    new Paragraph({
        tabStops: [
            {
                type: TabStopType.LEFT,
                position: convertInchesToTwip(2.9),
                leader: LeaderType.UNDERSCORE,
            },
            {
                type: TabStopType.LEFT,
                position: convertInchesToTwip(4.5),
                leader: LeaderType.UNDERSCORE,
            },
            {
                type: TabStopType.LEFT,
                position: convertInchesToTwip(6.6),
                leader: LeaderType.UNDERSCORE,
            },
        ],
        spacing: { before: 100, after: 220 },
        children: [
            new TextRun({ text: "Student name", size: THEME.size.body, color: THEME.color.text }),
            new TextRun({ text: "\t" }),
            new TextRun({ text: "    Class", size: THEME.size.body, color: THEME.color.text }),
            new TextRun({ text: "\t" }),
            new TextRun({ text: "    Date", size: THEME.size.body, color: THEME.color.text }),
            new TextRun({ text: "\t" }),
        ],
    });

const buildInstructionsParagraph = () => {
    const children = [
        new TextRun({
            text: "Tick each skill the student has demonstrated. Circle a proficiency for each level:  ",
            size: THEME.size.meta,
            color: THEME.color.muted,
        }),
    ];

    PROFICIENCY_LEGEND.forEach(({ abbr, label }, index) => {
        children.push(buildPillRun(abbr));
        children.push(
            new TextRun({
                text: ` ${label}` + (index < PROFICIENCY_LEGEND.length - 1 ? "     " : ""),
                size: THEME.size.meta,
                color: THEME.color.muted,
            })
        );
    });

    return new Paragraph({
        spacing: { before: 60, after: 220 },
        border: {
            bottom: { ...thinBorder(), space: 8 },
        },
        children,
    });
};

// ---------------------------------------------------------------
// DOK sections — header (badge, title, counter, proficiency
// selector) + one checkbox row per skill
// ---------------------------------------------------------------

const buildProficiencyBadgesParagraph = () =>
    new Paragraph({
        spacing: { before: 60 },
        children: PROFICIENCY_LEGEND.flatMap(({ abbr }, index) => {
            const run = buildPillRun(abbr);

            if (index === PROFICIENCY_LEGEND.length - 1) {
                return [run];
            }

            return [run, new TextRun({ text: "  ", size: THEME.size.badge })];
        }),
    });

const buildDokSectionTable = (dok) => {
    const total = dok.skills.length;

    const headerCell = new TableCell({
        shading: { type: ShadingType.CLEAR, fill: THEME.color.brandBg },
        margins: { top: 180, bottom: 160, left: 260, right: 260 },
        borders: NO_CELL_BORDERS,
        children: [
            new Paragraph({
                tabStops: [
                    {
                        type: TabStopType.RIGHT,
                        position: convertInchesToTwip(6.3),
                    },
                ],
                spacing: { after: 100 },
                children: [
                    buildPillRun(`DOK ${dok.level}`, { size: THEME.size.meta }),
                    new TextRun({
                        text: `   ${dok.title}`,
                        bold: true,
                        size: THEME.size.sectionTitle,
                        color: THEME.color.text,
                    }),
                    new TextRun({ text: "\t" }),
                    new TextRun({
                        text: `_____ of ${total} skill${total === 1 ? "" : "s"} demonstrated`,
                        size: THEME.size.meta,
                        color: THEME.color.muted,
                    }),
                ],
            }),
            buildProficiencyBadgesParagraph(),
        ],
    });

    const skillRows = dok.skills.map(
        (skill, skillIndex) =>
            new TableRow({
                children: [
                    new TableCell({
                        shading: {
                            type: ShadingType.CLEAR,
                            fill: skillIndex % 2 === 0 ? THEME.color.white : THEME.color.stripe,
                        },
                        margins: {
                            top: 110,
                            bottom: 110,
                            left: 300,
                            right: 260,
                        },
                        borders: NO_CELL_BORDERS,
                        children: [
                            new Paragraph({
                                children: [
                                    buildCheckboxRun(),
                                    new TextRun({
                                        text: "  " + skill.description,
                                        size: THEME.size.body,
                                        color: THEME.color.text,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            })
    );

    return new Table({
        width: fullWidth,
        borders: {
            top: thinBorder(),
            bottom: thinBorder(),
            right: thinBorder(),
            left: {
                style: BorderStyle.SINGLE,
                size: 28,
                color: THEME.color.brand,
            },
            insideHorizontal: thinBorder(),
            insideVertical: NO_BORDER,
        },
        rows: [new TableRow({ children: [headerCell] }), ...skillRows],
    });
};

// ---------------------------------------------------------------
// Overall proficiency + Teacher's note
// ---------------------------------------------------------------

const buildOverallProficiencyParagraph = () => {
    const labels = ["Emerging", "Developing", "Proficient", "Extending"];

    const children = [
        new TextRun({
            text: "Overall proficiency    ",
            bold: true,
            size: THEME.size.body,
            color: THEME.color.text,
        }),
    ];

    labels.forEach((label, index) => {
        children.push(buildCheckboxRun());
        children.push(
            new TextRun({
                text: " " + label + (index < labels.length - 1 ? "      " : ""),
                size: THEME.size.body,
                color: THEME.color.text,
            })
        );
    });

    return new Paragraph({
        spacing: { before: 280, after: 160 },
        children,
    });
};

const buildTeacherNoteSection = () => [
    new Paragraph({
        spacing: { before: 120, after: 160 },
        children: [
            new TextRun({
                text: "Teacher's note",
                bold: true,
                size: THEME.size.body,
                color: THEME.color.text,
            }),
        ],
    }),
    ...[0, 1, 2].map(
        () =>
            new Paragraph({
                spacing: { after: 320 },
                border: {
                    bottom: { ...thinBorder(), space: 10 },
                },
                children: [new TextRun({ text: " ", size: 2 })],
            })
    ),
];

const buildFooterParagraph = (generatedAt) =>
    new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200 },
        border: {
            top: {
                style: BorderStyle.SINGLE,
                size: 6,
                color: THEME.color.border,
                space: 8,
            },
        },
        children: [
            new TextRun({
                text: `Generated on ${formatDate(generatedAt)} • Gradely`,
                italics: true,
                size: THEME.size.meta,
                color: THEME.color.muted,
            }),
        ],
    });

// ---------------------------------------------------------------
// Document assembly
// ---------------------------------------------------------------

const generateDokChecklistDocument = (checklistData) => {
    const { subjectName, unitName, dokGroups, generatedAt } = checklistData;

    const children = [
        buildTitleParagraph(subjectName, unitName),
        buildIdentityParagraph(),
        buildInstructionsParagraph(),
    ];

    dokGroups.forEach((dok) => {
        children.push(buildDokSectionTable(dok));
        children.push(spacerParagraph());
    });

    children.push(buildOverallProficiencyParagraph());
    children.push(...buildTeacherNoteSection());
    children.push(buildFooterParagraph(generatedAt));

    return new Document({
        creator: "Gradely",
        title: `DOK Skills Checklist - ${unitName}`,
        styles: {
            default: {
                document: {
                    run: {
                        font: THEME.font,
                        size: THEME.size.body,
                        color: THEME.color.text,
                    },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        size: {
                            width: convertInchesToTwip(8.5),
                            height: convertInchesToTwip(11),
                        },
                        margin: {
                            top: convertInchesToTwip(0.6),
                            bottom: convertInchesToTwip(0.6),
                            left: convertInchesToTwip(0.75),
                            right: convertInchesToTwip(0.75),
                        },
                    },
                },
                children,
            },
        ],
    });
};

// ---------------------------------------------------------------
// Download — same technique as studentReportPdf.js
// (downloadStudentReportWord): Packer.toBlob() then a temporary
// <a download> anchor click.
// ---------------------------------------------------------------

export const downloadDokChecklistWord = async (checklistData) => {
    if (!checklistData || !checklistData.dokGroups?.length) {
        return;
    }

    const doc = generateDokChecklistDocument(checklistData);
    const blob = await Packer.toBlob(doc);

    const safeSubjectName = (checklistData.subjectName || "Subject").replace(
        /[^a-z0-9]+/gi,
        "_"
    );

    const safeUnitName = (checklistData.unitName || "Unit").replace(
        /[^a-z0-9]+/gi,
        "_"
    );

    const element = document.createElement("a");
    element.href = URL.createObjectURL(blob);
    element.download = `DOK_Checklist_${safeSubjectName}_${safeUnitName}.docx`;

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
};
